import { create } from "zustand";
import { persist } from "zustand/middleware";
import { Product, Stock } from "../types";
import { updateProductStocks } from "../services/stockService";

export interface CartItem extends Product {
  quantity: number;
  available_quantity?: number;
  is_low_stock?: boolean;
  current_warehouse_id?: number;
  // Дополнительные поля для корзины
  current_stock?: number; // Для совместимости с существующим кодом
}

interface CartStore {
  items: CartItem[];
  currentWarehouseId: number | null;
  isLoadingStocks: boolean;
  stockError: string | null;

  // Основные действия с корзиной
  setCart: (items: CartItem[]) => void;
  addItem: (item: Product, quantity?: number) => void;
  removeItem: (productId: number) => void;
  updateItemQuantity: (productId: number, quantityChange: number) => void;
  clearCart: () => void;

  // Действия с остатками
  setCurrentWarehouseId: (warehouseId: number) => void;
  updateItemStocks: (stocks: Stock[]) => void;
  updateAllItemStocks: (warehouseId: number) => Promise<void>;
  checkStockAvailability: () => {
    available: boolean;
    unavailableItems: CartItem[];
    lowStockItems: CartItem[];
  };
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: JSON.parse(localStorage.getItem("cart") || "[]"),
      currentWarehouseId: null,
      isLoadingStocks: false,
      stockError: null,

      // Основные действия с корзиной
      setCart: (items) =>
        set(() => {
          localStorage.setItem("cart", JSON.stringify(items));
          return { items };
        }),

      addItem: (item, quantity = 1) =>
        set((state) => {
          const existingItem = state.items.find((i) => i.id === item.id);
          let updatedItems;

          if (existingItem) {
            updatedItems = state.items.map((i) =>
              i.id === item.id
                ? {
                    ...i,
                    quantity: (i.quantity || 0) + quantity,
                  }
                : i
            );
          } else {
            updatedItems = [...state.items, { 
              ...item, 
              quantity,
              available_quantity: item.stock_quantity,
              current_warehouse_id: item.current_warehouse_id,
              is_low_stock: false
            }];
          }

          if (!localStorage.getItem("token")) {
            localStorage.setItem("cart", JSON.stringify(updatedItems));
          }
          return { items: updatedItems };
        }),

      removeItem: (productId) =>
        set((state) => {
          const updatedItems = state.items.filter((i) => i.id !== productId);
          localStorage.setItem("cart", JSON.stringify(updatedItems));
          return { items: updatedItems };
        }),

      updateItemQuantity: (productId, quantityChange) =>
        set((state) => {
          const updatedItems = state.items
            .map((i) =>
              i.id === productId
                ? {
                    ...i,
                    quantity: (i.quantity || 0) + quantityChange,
                  }
                : i
            )
            .filter((i) => (i.quantity || 0) > 0);

          localStorage.setItem("cart", JSON.stringify(updatedItems));
          return { items: updatedItems };
        }),

      clearCart: () => {
        localStorage.removeItem("cart");
        set({ items: [] });
      },

      // Действия с остатками
      setCurrentWarehouseId: (warehouseId) => 
        set({ currentWarehouseId: warehouseId }),

      updateItemStocks: (stocks) =>
        set((state) => {
          const updatedItems = state.items.map((item) => {
            const stock = stocks.find((s) => s.product_id === item.id);
            if (stock) {
              return {
                ...item,
                available_quantity: stock.quantity,
                current_warehouse_id: stock.warehouse_id,
                is_low_stock: (item.quantity || 0) > stock.quantity,
              };
            }
            return item;
          });

          localStorage.setItem("cart", JSON.stringify(updatedItems));
          return { items: updatedItems };
        }),

      updateAllItemStocks: async (warehouseId) => {
        const { items } = get();
        if (!items.length) return;

        try {
          set({ isLoadingStocks: true, stockError: null });
          
          const productIds = items.map((item) => item.id);
          const stocks = await updateProductStocks(warehouseId, productIds);
          
          get().updateItemStocks(stocks);
          set({ currentWarehouseId: warehouseId, isLoadingStocks: false });
        } catch (error) {
          set({ 
            stockError: error instanceof Error ? error.message : "Ошибка обновления остатков",
            isLoadingStocks: false 
          });
        }
      },

      checkStockAvailability: () => {
        const { items } = get();
        
        const unavailableItems: CartItem[] = [];
        const lowStockItems: CartItem[] = [];

        items.forEach((item) => {
          const hasStockData = item.available_quantity !== undefined;
          
          if (hasStockData) {
            // Если есть точные данные об остатках
            if (item.available_quantity === 0) {
              unavailableItems.push(item);
            } else if ((item.quantity || 0) > (item.available_quantity || 0)) {
              lowStockItems.push(item);
            }
          } else {
            // Если нет точных данных, используем is_available из API
            if (!item.is_available) {
              unavailableItems.push(item);
            }
          }
        });

        return {
          available: unavailableItems.length === 0 && lowStockItems.length === 0,
          unavailableItems,
          lowStockItems,
        };
      },
    }),
    {
      name: "cart-storage",
      partialize: (state) => ({
        items: state.items,
        currentWarehouseId: state.currentWarehouseId,
      }),
    }
  )
);
